import type {
    ContentGrader,
    GradingContext,
    GradingResult,
    ScoringConfig,
} from '@ai-content-hub/content-core';
import { runGradingSafely } from '@ai-content-hub/content-core';
import { contentService, generateService } from '@ai-content-hub/shared-api';
import { tiptapToMarkdown, tiptapJsonToPlainText } from '@ai-content-hub/content-kit';
import type { TiptapJSON } from '@ai-content-hub/content-kit';
import type { EssayData } from './schema';
import { composeEssayQuestionContent, extractTextFromLessonContent } from './utils';

const VALID_QUESTION_TYPES = [
    'QT_LISTEN_SUMMARIZE',
    'QT_WRITE_SUMMARIZE_WRITTEN_TEXT',
    'QT_WRITE_ESSAY',
] as const;

function countWords(text: string): number {
    if (!text || !text.trim()) return 0;
    return text.trim().split(/\s+/).length;
}

function getQuestionType(data: EssayData): string {
    const q = data.essayQuestion as { gradingQuestionType?: string };
    const code = q?.gradingQuestionType;
    if (code && VALID_QUESTION_TYPES.includes(code as (typeof VALID_QUESTION_TYPES)[number])) {
        return code;
    }
    return 'QT_WRITE_ESSAY';
}

/**
 * ELMS-2377: grade-jobs already created by a deferred (host FORCE_SUBMIT) submit, keyed by
 * content + answer text. A repeat FORCE_SUBMIT after the first flight finished (LMS retry, lost
 * ack) must not start another billable job for the SAME answer; a changed answer, or a failed
 * earlier start (never stored), creates a new one. Lives as long as the iframe page.
 */
const deferredEssayJobs = new Map<string, { job_id: string; draft?: boolean }>();

/** Test hook. */
export function resetDeferredEssayJobs(): void {
    deferredEssayJobs.clear();
}

export type EssayAnswer = { text: string; richText?: TiptapJSON; wordCount?: number };

export function validateEssayAnswer(data: EssayData, answer: unknown): boolean {
    if (!answer || typeof answer !== 'object') return false;
    const text = (answer as EssayAnswer).text ?? '';
    // Allow submission as long as there is any text — min/max word count is a soft UI hint,
    // not a hard gate. This ensures the answer is always saved even if below minWords.
    return text.trim().length > 0;
}

export async function gradeEssay(
    data: EssayData,
    answer: EssayAnswer,
    scoring: ScoringConfig | undefined,
    context: GradingContext | undefined
): Promise<GradingResult> {
    const contentId = context?.contentId;
    if (!contentId) {
        throw new Error('Essay grading requires contentId in grading context');
    }
    const language = context?.config?.i18n?.defaultLanguage ?? 'vi';

    // Prefer rich text → markdown for AI; fall back to plain text
    let essayText: string;
    if (answer.richText && typeof answer.richText === 'object' && answer.richText.type === 'doc') {
        essayText = tiptapToMarkdown(answer.richText).trim();
        if (!essayText) {
            essayText = tiptapJsonToPlainText(answer.richText).trim();
        }
    } else {
        essayText = (answer.text ?? '').trim();
    }
    const wordCount = answer.wordCount ?? countWords(answer.text ?? essayText);
    // The essay task (`essayQuestion.question`) is what the student answers, so it is the
    // primary question; the lesson content is supporting reference material (ELMS-2280).
    let taskText = '';
    const q = data.essayQuestion?.question;
    if (typeof q === 'string') {
        taskText = q.trim();
    } else if (q && typeof q === 'object' && q.type === 'doc') {
        taskText = tiptapJsonToPlainText(q).trim();
    }
    let questionContent = composeEssayQuestionContent(
        taskText,
        extractTextFromLessonContent(data.lessonContent)
    );
    // Last resort: use the essay title/description
    if (!questionContent) {
        questionContent = (data.title || data.description || '').trim();
    }
    const questionType = getQuestionType(data);

    const gradeRequest = {
        user_answer: essayText,
        question_type: questionType,
        language,
        question_content: questionContent || undefined,
    };

    // Start a background grading job and poll for the result
    // WHY: In multi-player mode, a sessionToken is passed in context so the grader
    // can call the grade-job endpoint with Session auth instead of the global view-token.
    // EAA-2117: a saved question keeps `/content/<id>/essay/grade-job`; an unsaved draft
    // (question-bank `/new` preview, client id) goes to `/content/draft/grade-job` with
    // `content_type` + `tenant_id`. That path is authors-only, so it never carries — and the
    // poll below never uses — a learner session token.
    const startJob = () =>
        contentService.startEssayGradeJob(contentId, gradeRequest, {
            sessionToken: context?.sessionToken,
            tenantId: context?.tenantId,
        });
    // ELMS-2377: host FORCE_SUBMIT — the job only has to EXIST on the server (it carries the
    // session id, so the backend stamps the score on the session when it completes). Do not poll.
    // If creating it fails, fall back to the synchronous path below (start again + wait), so a
    // network blip never costs the learner the AI score.
    let started: Awaited<ReturnType<typeof startJob>> | undefined;
    if (context?.deferAiResult && context.sessionToken) {
        const memoKey = `${contentId}\u0000${essayText}`;
        if (deferredEssayJobs.has(memoKey)) {
            return { isCorrect: false, score: 0, max: 100, pending: true };
        }
        try {
            started = await startJob();
        } catch (err) {
            console.warn('[essay] deferred grade-job start failed, falling back to waiting', err);
        }
        if (started && !started.draft) {
            deferredEssayJobs.set(memoKey, started);
            return { isCorrect: false, score: 0, max: 100, pending: true };
        }
    }
    const { job_id, draft } = started ?? (await startJob());

    const finalJob = await generateService.pollJobStatus(
        job_id,
        () => {
            // onStatusChange — no-op; we just wait for terminal state
        },
        {
            interval: 2000,
            maxAttempts: 60,
            pendingTimeoutMs: 30000, // 2s × 60 = 120s max, 30s pending limit
            // WHY: Student tokens lack job.read permission; poll with Session auth.
            sessionToken: draft ? undefined : context?.sessionToken,
        },
    );

    if (finalJob.status === 'failed') {
        throw new Error(finalJob.error || 'Essay grading job failed');
    }

    const apiResult = (finalJob.result ?? {}) as Record<string, unknown>;

    const percentage = (apiResult.overall_score_percentage as number) ?? 0;
    const scoringConfig = scoring as { passingScore?: number; passingThreshold?: number } | undefined;
    const passingScore = scoringConfig?.passingScore ?? 0.6;
    const passingThreshold = scoringConfig?.passingThreshold ?? passingScore;
    const isCorrect = percentage >= passingThreshold * 100;

    return {
        isCorrect,
        score: percentage,
        max: 100,
        details: apiResult as unknown as Record<string, unknown>,
    };
}

export const essayContentGrader: ContentGrader<EssayData, EssayAnswer> = {
    // ELMS-2369: server/technical errors never reach the learner (friendly i18n text instead).
    grade: (data, answer, scoring, context) =>
        runGradingSafely('essay', () => gradeEssay(data, answer ?? { text: '' }, scoring, context)),
    requiresNetwork: () => true,
    validateAnswer: (answer, data) => validateEssayAnswer(data, answer),
};
